// #3008 -- pure three-way merge for a stale_write auto-resubmit. See
// .grind/3008/design.md "Merge rules" for the full contract this
// implements: a two-way diff (local vs. server only) can't tell "scribe
// changed this field" from "server changed this field", so this reads a
// tracked common ancestor too. No I/O, no local/db.js or sync.js imports --
// ancestor/local/server are plain sheet-shaped objects the caller (sync.js,
// step 3, not this file) reads off IndexedDB (`_syncedSheet`) and the 409
// response body (`err.response.data.data.sheet`).

// score-api owns these -- never resolved by diffing, always taken straight
// from the server's current sheet. Mirrors useScoreSheet.js's own
// SERVER_MANAGED strip list (toBody() already never lets `local` carry
// these, this is belt-and-braces so the merge is correct even if it ever
// did). Never compute a score here -- totals are always a verbatim copy of
// `server`'s value, never derived.
//
// `percentage` is deliberately NOT in this list. useScoreSheet.js's
// scoreSheetIsClientTotal (the ONE place that decides this, #1750) says a
// judge-percentage sheet has no movement math to derive a total from -- the
// scribe TYPES the percentage directly, so it is an ordinary scribe-owned
// field for that one scoring model, not server-computed. Always taking it
// from `server` here would silently drop a judge-percentage scribe's typed
// score out from under an auto-merge (found in judge round 1 review).
// `merge()`'s `clientTotal` option (the caller passes
// scoreSheetIsClientTotal(test), never re-derived here) decides per call
// whether `percentage` diffs like any other field or is folded into this
// server-managed list.
const SERVER_MANAGED_FIELDS = [
  "version",
  "createdAt",
  "updatedAt",
  "totalPoints",
  "maxPoints",
  "signatures",
];

function isPlainObject(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// Exported for db.js's bulkPutAncestorAware (#3008 judge round 1 B1, rule 1):
// it needs the exact same structural-equality check this merge already uses
// to decide whether an existing row's content still matches its own tracked
// ancestor, rather than a second, possibly-diverging implementation.
export function deepEqual(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    return ak.every(
      (k) => Object.prototype.hasOwnProperty.call(b, k) && deepEqual(a[k], b[k]),
    );
  }
  return false;
}

// Candidate identity keys for matching array-of-object items (movements,
// collectives) across the three snapshots, in preference order.
const ARRAY_ITEM_KEYS = ["movementNumber", "id", "key"];

function arrayItemKeyName(arr) {
  if (!Array.isArray(arr) || arr.length === 0 || !isPlainObject(arr[0])) return null;
  return ARRAY_ITEM_KEYS.find((k) => Object.prototype.hasOwnProperty.call(arr[0], k)) ?? null;
}

function indexByKey(arr, keyName) {
  const map = new Map();
  for (const item of arr) map.set(item[keyName], item);
  return map;
}

// #3008 judge round 1 (non-blocking finding) -- indexByKey's Map silently
// collapses two items that share a key (the second overwrites the first) or
// that are both missing the key (both land on the same `undefined` slot).
// When that happens to leave the SAME set of unique keys across all three
// sides (e.g. one side has an extra duplicate-keyed item beyond what the
// other two sides have), diff3Array's own "every side has every key" guard
// below never fires, and the duplicate item's real content silently
// vanishes while the loop over the RAW array re-emits the survivor twice.
// Validate every RAW item in `arr` -- not the deduped Map -- has a defined,
// unique value at `keyName` before any side's array is trusted for keyed
// matching at all. A missing or duplicate key is always a conflict, never
// a guess about which item won.
function arrayKeysAreSound(arr, keyName) {
  const seen = new Set();
  for (const item of arr) {
    if (!isPlainObject(item) || item[keyName] === undefined) return false;
    if (seen.has(item[keyName])) return false;
    seen.add(item[keyName]);
  }
  return true;
}

// Three-way diff of one value at `path`. Returns { value, conflicts }.
// `value` is only ever a verbatim copy of `local`/`server`/`ancestor` (or a
// structural recombination of such copies, one level into a nested
// object/array-of-objects) -- never a derived/invented value. A field left
// out of `value` (undefined) means this function refused to pick a side
// for it; `conflicts` names why.
function diff3(ancestor, local, server, path) {
  const localChanged = !deepEqual(local, ancestor);
  const serverChanged = !deepEqual(server, ancestor);

  if (!localChanged && !serverChanged) return { value: ancestor, conflicts: [] };
  if (localChanged && !serverChanged) return { value: local, conflicts: [] };
  if (!localChanged && serverChanged) return { value: server, conflicts: [] };
  // Both changed.
  if (deepEqual(local, server)) return { value: local, conflicts: [] };

  // Both changed, to different values. Before giving up as a whole-field
  // conflict, try one level of structural recursion so a nested per-
  // movement edit on one side doesn't collide with an unrelated per-
  // movement edit on the other (design.md's "nested per-movement fields").
  if (isPlainObject(ancestor) && isPlainObject(local) && isPlainObject(server)) {
    return diff3Object(ancestor, local, server, path);
  }
  const keyName =
    arrayItemKeyName(ancestor) || arrayItemKeyName(local) || arrayItemKeyName(server);
  if (keyName && Array.isArray(ancestor) && Array.isArray(local) && Array.isArray(server)) {
    const merged = diff3Array(ancestor, local, server, keyName, path);
    if (merged) return merged;
  }

  return { value: undefined, conflicts: [path] };
}

function diff3Object(ancestor, local, server, path) {
  const keys = new Set([...Object.keys(ancestor), ...Object.keys(local), ...Object.keys(server)]);
  const value = {};
  const conflicts = [];
  for (const k of keys) {
    const result = diff3(ancestor[k], local[k], server[k], `${path}.${k}`);
    if (result.value !== undefined) value[k] = result.value;
    conflicts.push(...result.conflicts);
  }
  return { value, conflicts };
}

function diff3Array(ancestor, local, server, keyName, path) {
  // A missing or duplicate key on ANY raw item, on any side, is a conflict
  // for the whole array -- never a silent collapse (see arrayKeysAreSound's
  // own comment above for why the set-membership check below can't catch
  // this alone).
  if (
    !arrayKeysAreSound(ancestor, keyName) ||
    !arrayKeysAreSound(local, keyName) ||
    !arrayKeysAreSound(server, keyName)
  ) {
    return null;
  }
  const aMap = indexByKey(ancestor, keyName);
  const lMap = indexByKey(local, keyName);
  const sMap = indexByKey(server, keyName);
  const allKeys = new Set([...aMap.keys(), ...lMap.keys(), ...sMap.keys()]);
  // If any side is missing an item another side has, the item SET itself
  // changed (a movement added/removed), not just an edit within one --
  // outside this card's scope. Fall back to a whole-field conflict rather
  // than guessing which item won or lost.
  for (const k of allKeys) {
    if (!aMap.has(k) || !lMap.has(k) || !sMap.has(k)) return null;
  }
  const value = [];
  const conflicts = [];
  // Preserve local's item order.
  for (const item of local) {
    const k = item[keyName];
    const result = diff3(aMap.get(k), lMap.get(k), sMap.get(k), `${path}[${keyName}=${k}]`);
    if (result.value !== undefined) value.push(result.value);
    conflicts.push(...result.conflicts);
  }
  return { value, conflicts };
}

/**
 * Pure three-way merge of a scribe's queued sheet edit against the
 * server's current sheet, on a stale_write 409 (#3008). See
 * .grind/3008/design.md "Merge rules".
 *
 * - `ancestor`: the tracked common ancestor (`_syncedSheet`) the queued
 *   edit's body was built against.
 * - `local`: the queued op's own body (`toBody()`'s output -- only fields a
 *   scribe edit ever sends; never version/timestamps/totals/percentage).
 * - `server`: the server's CURRENT sheet, from the 409 fail body
 *   (`err.response.data.data.sheet`).
 *
 * Per field, nested per-movement fields included: only local changed ->
 * local; only server changed -> server; both changed to the same value ->
 * that value; both changed to different values -> conflict (that field is
 * left out of `merged` and its path recorded in `conflicts`, no invented
 * value). Server-managed fields (version, timestamps, computed
 * totals/percentage, signatures) always come from `server`, never diffed --
 * this function never computes a score.
 *
 * Returns `{ merged, conflicts }`. `conflicts` is a list of field paths
 * (dotted for nested objects, `field[key=id]` for matched array items) --
 * a non-empty list means the whole op should be flagged for review, not
 * partially resubmitted (the caller's decision, not this function's).
 *
 * `clientTotal` (default false): pass `scoreSheetIsClientTotal(test)` --
 * useScoreSheet.js's own predicate, never re-derived here -- when the sheet
 * being merged belongs to the one scoring model (judge-percentage) whose
 * scribe TYPES `percentage` as the score of record. When true, `percentage`
 * is diffed like any other scribe-owned field instead of being taken
 * unconditionally from `server`.
 */
export function merge(ancestor, local, server, { clientTotal = false } = {}) {
  const a = ancestor ?? {};
  const l = local ?? {};
  const s = server ?? {};
  const keys = new Set([...Object.keys(a), ...Object.keys(l), ...Object.keys(s)]);
  const merged = {};
  const conflicts = [];
  const serverManagedFields = clientTotal
    ? SERVER_MANAGED_FIELDS
    : [...SERVER_MANAGED_FIELDS, "percentage"];

  for (const k of keys) {
    if (serverManagedFields.includes(k)) {
      if (s[k] !== undefined) merged[k] = s[k];
      continue;
    }
    const result = diff3(a[k], l[k], s[k], k);
    if (result.value !== undefined) merged[k] = result.value;
    conflicts.push(...result.conflicts);
  }

  return { merged, conflicts };
}
